import { FormValueScope } from '@epages/react-components'
import type { MouseEventHandler } from 'react'
import { kebabCase } from 'lodash-es'
import { useSelector } from 'react-redux'
import cc from 'classcat'

import { Tooltip } from '../Tooltip'
import { getNormalizedHeaderType } from '../../../utils/themes'
import { useTranslate } from '../../utils/translate'
import FormGroup from '../formComponents/FormGroup'
import FormValue from '../formComponents/FormValue'

type HeaderPreviewProps = {
  className: string
  onClick: MouseEventHandler
}

function HeaderPreview({ className, onClick }: Readonly<HeaderPreviewProps>) {
  return (
    <div className={className} onClick={onClick} data-testid="headerPreview">
      <div className="space"></div>
      <div className="logo"></div>
      <div className="burger-menu"></div>
      <div className="menu"></div>
      <div className="search"></div>
      <div className="customer"></div>
      <div className="cart"></div>
    </div>
  )
}

export default function HeaderTypeSettings() {
  const t = useTranslate('interface', 'components.bottomBar.headerSettings.themeHeader')

  const isEcommerceDisabled = useSelector<State, boolean>((state) =>
    Boolean(
      state
        .getIn(['shop', 'attributes'])
        .find((attribute) => attribute.get('name') === 'ecommerce:disabled' && attribute.get('value') === 'true'),
    ),
  )

  // We know that every theme has a theme settings group "themeHeader" with a radio buttons setting "headerType".
  const themeHeaderTypeSetting = useSelector<State, Theme.RadioButtonsSettingsSchemaItem>(
    (state) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (state.get('themeSettingsSchema').toJS() as Theme.SettingsSchema)
        .find((group) => group.name === 'themeHeader')!
        .settings.find((item) => item.name === 'headerType') as Theme.RadioButtonsSettingsSchemaItem,
  )

  return (
    <>
      <div className="dali-settingslayer-element">{t('description')}</div>
      <div className="dali-settingslayer-element">
        <FormValueScope name="themeHeader">
          <FormValue name="headerType">
            {(themeHeaderType, setThemeHeaderType) =>
              themeHeaderTypeSetting.options.map((headerType) => (
                <div key={headerType} className="ep-header-types-settings">
                  <FormGroup
                    name="headerType"
                    className="ep-header-types-settings-form"
                    label={t(`headerTypes.${headerType}.label`)}
                    // Temporarily disable the info bubble for the transition
                    // phase of new modular headers. See EPUI-1151.
                    // info={t(`headerTypes.${headerType.value}.explanation`)}
                    tagName="label"
                    labelAfterComponent
                  >
                    <input
                      type="radio"
                      name="headerType"
                      value={headerType}
                      checked={themeHeaderType === headerType}
                      onChange={(event) => setThemeHeaderType(event.target.value)}
                    />
                  </FormGroup>
                  <div className="ep-header-types-settings-tooltip">
                    <Tooltip
                      hideIcon
                      sideOffset={10}
                      trigger={
                        <HeaderPreview
                          className={cc({
                            [`header-type-${kebabCase(headerType)}`]: true,
                            'hide-grid-items': isEcommerceDisabled,
                          })}
                          onClick={() => setThemeHeaderType(headerType)}
                        />
                      }
                      content={t(`headerTypes.${headerType}.explanation`)}
                    />
                  </div>
                </div>
              ))
            }
          </FormValue>
        </FormValueScope>
      </div>
    </>
  )
}
HeaderTypeSettings.defaultValue = {}

/*
 * Replace old header values with new ones.
 * Old header values are still saved in the database, and will be for an unforeseeable future.
 * To mark the new headertype as checked we have to replace the form data value in `prepare`.
 */
HeaderTypeSettings.prepare = (value: ImmutableMap) => {
  return value.set('headerType', getNormalizedHeaderType(value.get('headerType')))
}
